import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert as MuiAlert,
  Container,
  Grid,
  Paper,
  Snackbar,
  Typography
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import PageHeader from '@/content/Management/Receivables/PageHeader';
import ReceivablesSummary from '@/content/Management/Receivables/ReceivablesSummary';
import ReceivablesTable from '@/content/Management/Receivables/ReceivablesTable';
import ReceivableDetailDialog from '@/content/Management/Receivables/ReceivableDetailDialog';
import { receivableService } from '@/data/receivableService';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { Filters } from '@/types/Filters';
import {
  CustomerReceivable,
  ReceivableStatus
} from '@/types/CustomerReceivable.interface';

/**
 * Açık Hesaplar: what customers owe the currently selected branch. Branch
 * scoped like the other branch pages (a branch switch reloads the app, so the
 * branch id read at render time is always current). Collections (Cari
 * Tahsilat) are made at the POS; the admin only reads and, for an untouched
 * receivable, cancels.
 */
const ReceivablesManagement = () => {
  const { t } = useTranslation();
  const { currentBranch } = useUserViewMode();
  const branchId = currentBranch?.id;

  const [receivables, setReceivables] = useState<CustomerReceivable[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalOutstanding, setTotalOutstanding] = useState(0);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lastFilters, setLastFilters] = useState<Filters | undefined>();

  const [selected, setSelected] = useState<CustomerReceivable | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  // Only the answer of the latest request may touch the state: chips and the
  // search box can fire several requests that finish out of order.
  const listRequestId = useRef(0);
  const summaryRequestId = useRef(0);

  /**
   * The summary cards describe what is still owed (OPEN and PARTIAL), whatever
   * the status chip or the search box say, so BOTH numbers come from the same
   * two status only requests (`limit: 1`, only the totals are read) instead of
   * the filtered listing, whose `totalOutstanding` follows the active filters.
   * It is loaded on mount, on refresh and after a cancellation, not on every
   * filter change.
   */
  const fetchSummary = useCallback(async () => {
    if (!branchId) return;
    const requestId = ++summaryRequestId.current;
    try {
      const [open, partial] = await Promise.all([
        receivableService.list(branchId, { status: 'OPEN', page: 0, limit: 1 }),
        receivableService.list(branchId, {
          status: 'PARTIAL',
          page: 0,
          limit: 1
        })
      ]);
      if (requestId !== summaryRequestId.current) return;
      setOpenCount((open.total ?? 0) + (partial.total ?? 0));
      setTotalOutstanding(
        (Number(open.totalOutstanding) || 0) +
          (Number(partial.totalOutstanding) || 0)
      );
    } catch {
      // The cards are informational; the listing reports its own errors.
    }
  }, [branchId]);

  useEffect(() => {
    void fetchSummary();
  }, [fetchSummary]);

  const fetchReceivables = async (filters?: Filters) => {
    setLastFilters(filters);
    if (!branchId) return;
    const requestId = ++listRequestId.current;
    try {
      setLoading(true);
      // 0 based page, as the backend expects; `search` maps to `customer`.
      const response = await receivableService.list(branchId, {
        status: (filters?.status as ReceivableStatus | undefined) || undefined,
        customer: filters?.search || undefined,
        page: filters?.page ?? 0,
        limit: filters?.limit ?? 10
      });
      if (requestId !== listRequestId.current) return;
      setReceivables(response.items ?? []);
      setTotalCount(response.total ?? 0);
    } catch (err: any) {
      if (requestId !== listRequestId.current) return;
      setError(err?.response?.data?.message || t('receivables.error.fetch'));
    } finally {
      if (requestId === listRequestId.current) setLoading(false);
    }
  };

  const handleFilterChange = (filters: Filters) => {
    void fetchReceivables(filters);
  };

  const handleVoided = () => {
    setSelected(null);
    setSuccessMessage(t('receivables.void.success'));
    void fetchReceivables(lastFilters);
    void fetchSummary();
  };

  return (
    <>
      <Helmet>
        <title>{t('receivables.title')}</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader />
      </PageTitleWrapper>

      {branchId ? (
        <>
          <Container
            disableGutters
            maxWidth={false}
            sx={{ maxWidth: '95%', mb: 3 }}
          >
            <ReceivablesSummary
              totalOutstanding={totalOutstanding}
              openCount={openCount}
            />
          </Container>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <ReceivablesTable
                receivables={receivables}
                loading={loading}
                totalCount={totalCount}
                onFilterChange={handleFilterChange}
                onSelectReceivable={setSelected}
                onRefresh={() => void fetchSummary()}
                pageKey="receivables"
              />
            </Grid>
          </Grid>
        </>
      ) : (
        <Container disableGutters maxWidth={false} sx={{ maxWidth: '95%' }}>
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {t('branch.not.selected')}
            </Typography>
          </Paper>
        </Container>
      )}

      <ReceivableDetailDialog
        open={Boolean(selected)}
        receivable={selected}
        onClose={() => setSelected(null)}
        onVoided={handleVoided}
      />

      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(undefined)}
      >
        <MuiAlert
          variant="filled"
          severity="success"
          onClose={() => setSuccessMessage(undefined)}
        >
          <Typography>{successMessage}</Typography>
        </MuiAlert>
      </Snackbar>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={() => setError(undefined)}
      >
        <MuiAlert
          variant="filled"
          severity="error"
          onClose={() => setError(undefined)}
        >
          <Typography>{error}</Typography>
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default ReceivablesManagement;
